import styled from 'styled-components';
import { Field as LegoField } from '@ContextLogic/lego';

/* Mixins */
import {
  BoxSizingSelfAndChildren,
  FlexDirectionColumn,
  WebFontBody1,
  WebFontBody2,
  WebFontCallout1,
  WebFontCallout2,
  WebFontHeader2Bold,
  WebFontHeader3,
  WebFontHeader4,
  WebFontHeader5,
  WebFontHeader6,
  FlexCenterChildren,
  WebFontCaption1,
  withLineHeight,
} from 'styles/Mixins';

/* Utils */
import { assignIfDefined, mediaQuery } from 'styles/Utils';

/* Types */
import type { CSSProperties } from 'react';

import { DESKTOP_HEADER_HEIGHT } from 'styles/landing/LandingPage';

const CONTAINER_WIDTH = '80%';
const MAX_CONTAINER_WIDTH = '1000px';
const SMALL_MAX_CONTAINER_WIDTH = '536px';

export const Container = styled.div<{
  stretch?: boolean;
  size?: string;
  flex?: boolean;
}>`
  width: ${({ stretch }) =>
    stretch ? 'calc(100% - 32px);' : `${CONTAINER_WIDTH};`};
  max-width: ${({ size }) =>
    size === 'small' ? SMALL_MAX_CONTAINER_WIDTH : MAX_CONTAINER_WIDTH};
  ${({ flex }) =>
    flex &&
    'display: flex; align-items: center; justify-content: space-between;'};
`;

export const Wrapper = styled.div`
  height: 100%;
  ${FlexDirectionColumn}
  align-items: center;
  ${BoxSizingSelfAndChildren}
  color: ${({ theme }) => theme.Grey1};
`;

export const Section = styled.div<{
  shouldRenderMobile?: boolean;
  flex?: boolean;
  noBottomPadding?: boolean;
  noBottomBorder?: boolean;
  fillHeight?: boolean;
  centerTextAlign?: boolean;
}>`
  padding-top: ${({ shouldRenderMobile }) => (shouldRenderMobile ? 32 : 64)}px;
  padding-bottom: ${({ noBottomPadding, shouldRenderMobile }) => {
    if (noBottomPadding) {
      return 0;
    }
    return shouldRenderMobile ? 32 : 64;
  }}px;
  border-bottom: ${({ noBottomBorder }) => (noBottomBorder ? 0 : 1)}px solid
    ${({ theme }) => theme.Grey5};
  ${({ shouldRenderMobile }) =>
    shouldRenderMobile && 'display: flex; flex-direction: column;'};
  ${({ fillHeight }) =>
    fillHeight && `min-height: calc(100vh - ${DESKTOP_HEADER_HEIGHT}px);`};
  ${({ centerTextAlign }) => centerTextAlign && 'text-align: center;'};
`;

export const Field = styled(LegoField)<{ margin?: string }>`
  flex: 1;
  ${assignIfDefined('margin')};
`;

export const FormSection = styled.div<{
  stretch?: boolean;
  flex?: boolean;
}>`
  ${({ stretch }) => stretch && 'width: 100%'};
  ${({ flex }) => flex && 'display: flex'};

  ${mediaQuery.desktop`
    max-width: ${SMALL_MAX_CONTAINER_WIDTH}px;
  `}
  ${mediaQuery.mobile`
    max-width: 100%;
  `}
`;

export const CallToActionTitle = styled.h1`
  ${mediaQuery.desktop`
    ${WebFontHeader2Bold}
    ${withLineHeight}
  `}
  ${mediaQuery.mobile`
    ${WebFontHeader3}
  `}
`;

export const CallToActionSubtitle = styled.h2`
  ${WebFontBody1}
  ${mediaQuery.desktop`
    line-height: 48px;
  `}
  ${mediaQuery.mobile`
    margin-top: 16px;
  `}
`;

export const PageTitle = styled.h2<{
  center?: boolean;
}>`
  ${({ center }) => center && 'text-align: center;'};
  ${mediaQuery.desktop`
    ${WebFontHeader4}
    ${withLineHeight}
  `}
  ${mediaQuery.mobile`
    ${WebFontCallout1}
  `}
`;

export const PageSubtitle = styled.h2<{
  center?: boolean;
}>`
  color: ${({ theme }) => theme.Grey2};
  ${({ center }) => center && 'text-align: center;'};
  ${mediaQuery.desktop`
    ${WebFontBody1}
    ${withLineHeight}
    margin-bottom: 32px;
  `}
  ${mediaQuery.mobile`
    ${WebFontBody2}
    margin-bottom: 16px;
  `}
`;

export const PageFooter = styled.div<{ justifyStart?: boolean }>`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: ${({ justifyStart }) =>
    justifyStart ? 'flex-start' : 'flex-end'};
`;

export const LegalText = styled.div<{ centerTextAlign?: boolean }>`
  ${WebFontBody2};
  ${({ centerTextAlign }) => centerTextAlign && 'text-align: center;'};
  line-height: 18px;
  margin-bottom: 12px;
`;

export const LegalLink = styled.a`
  color: ${({ theme }) => theme.darkCobalt};
`;

export const ClickableText = styled.div`
  ${WebFontCaption1}
  color: ${({ theme }) => theme.Grey3};
  cursor: pointer;
  align-self: center;
`;

export const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.white};
  background-color: ${({ theme }) => theme.red};
  ${mediaQuery.desktop`
    ${WebFontCallout1};
  `}
  ${mediaQuery.mobile`
    ${WebFontCallout2};
  `}
  text-align: center;
  padding: 10px 0px;
  width: 100%;
`;

export const SectionHeaderWrapper = styled.div`
  ${FlexCenterChildren};
  ${mediaQuery.desktop`
    margin-bottom: 32px;
  `}
  ${mediaQuery.mobile`
    margin-bottom: 16px;
  `}
  > * {
    &:first-child {
      display: flex;
      margin-right: auto;
      flex: 1;
      justify-content: flex-start;
    }
    &:last-child {
      display: flex;
      margin-left: auto;
      flex: 1;
    }
  }
`;

export const SectionHeaderArrowWrapper = styled.div`
  ${mediaQuery.desktop`
    padding-right: 32px;
  `}
  ${mediaQuery.mobile`
    padding-right: 16px;
  `}
`;

export const Link = styled.a`
  ${mediaQuery.desktop`
    ${WebFontCallout1};
  `}
  ${mediaQuery.mobile`
    ${WebFontCallout2};
  `}
  color: ${({ theme }) => theme.darkCobalt};
  padding-left: 0.5em;
  :hover {
    text-decoration: underline;
  }
`;

export const HoursContainer = styled.div``;

export const DayName = styled.div``;

export const DayHours = styled.div``;

export const DayRow = styled.div`
  margin: 16px 0px 12px 0px;
  display: grid;
  grid-template-columns: 0.5fr 2fr 2fr 0.5fr 2fr;
  justify-items: center;
  align-items: center;
`;

export const OptionalHourLabel = styled.div`
  color: ${({ theme }) => theme.Grey3};
`;

export const PaddedContainer = styled.div`
  padding: 0px 25px;
`;

export const SuccessText = styled.h1`
  ${mediaQuery.desktop`
    ${WebFontHeader4};
  `}
  ${mediaQuery.mobile`
    ${WebFontHeader5};
  `}
  text-align: center;
`;

export const SectionBorder = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.Grey5};
  margin: 40px 0px;
`;

export const SectionSubtitle = styled.div<{
  centerTextAlign?: boolean;
  topMarginSmall?: boolean;
}>`
  ${WebFontBody1};
  ${withLineHeight};
  ${({ centerTextAlign }) => centerTextAlign && 'text-align: center;'};
  ${({ topMarginSmall }) => topMarginSmall && 'margin-top: 16px'};
`;

export const Step = styled.div`
  width: 40px;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.Grey4};
  color: ${({ theme }) => theme.Grey3};
  border-radius: 100%;
  ${FlexCenterChildren};
  background-color: #fff;
  ${WebFontHeader4};
`;

export const Steps = styled.div``;

export const StepWrapper = styled.div`
  justify-content: flex-start;
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  :first-child {
    margin-top: 20px;
    margin-bottom: 8px;
  }
`;

export const StepIconWrapper = styled.div``;

export const StepIcon = styled(Step)`
  color: ${({ theme }) => theme.darkCobalt};
  background-color: ${({ theme }) => theme.lightCobalt};
  border: none;
  margin-right: 10px;
  ${WebFontHeader6}
`;

export const CheckWrapper = styled.div`
  ${FlexCenterChildren};
  padding: 8px;
`;

export const AppLinkWrapper = styled.div`
  margin: 0px 0px 15px 40px;
  display: flex;

  ${mediaQuery.mobile`
    flex-direction: column;
  `}
`;

export const AppStoreLink = styled.div`
  height: 50px;
  width: 120px;
  margin: 0 12px;
`;

export const SelectInputWrapper = styled.div<{ [props: string]: any }>`
  width: 100%;
  ${({ withTopMargin }) => withTopMargin && 'margin-top: 16px;'};
  ${({ withRightMargin }) => withRightMargin && 'margin-right: 16px;'};
  ${({ withLeftMargin }) => withLeftMargin && 'margin-left: 16px;'};
`;

export const ImageContainer = styled.div<{ margin?: string }>`
  ${FlexCenterChildren};
  ${assignIfDefined('margin')};
`;

export const Image = styled.img<{ style?: CSSProperties }>`
  width: ${({ style }) => style && style.width};
`;
